// Plan madde 8.1 — typed envelope unwrap helper for openapi-fetch results.
//
// Frontend hooks call the operator-graph client (apps/operator-console/src/
// api/operator-graph.ts) and receive openapi-fetch's `{ data, error, response }`
// triple. Without this helper, every hook would manually decide:
//   * is `result.data?.data` non-null? → success → return it
//   * else → look at result.error / result.response — is `errors[]` populated? → failure → throw
//   * else → some malformed response — throw what?
//
// `unwrap()` codifies the spec 58 envelope contract:
//   * SUCCESS: HTTP 2xx + body { data: T (non-null), meta }, no errors[]  → return data
//   * FAILURE: HTTP 4xx/5xx + body { data: null, meta, errors: ErrorEntry[] }
//             → throw EnvelopeError carrying the full envelope error array
//   * MALFORMED: any other shape (empty body, mixed shapes, missing data &
//             no errors) → throw EnvelopeError with synthetic
//             platform.invariant_violation entry so the caller's catch block
//             still gets a structured error
//
// EnvelopeError thrown here is what plan madde 8.2's mapErrorToToast()
// adapter consumes — it walks `error.errors[]`, picks classification +
// retry hints, and renders a localized toast keyed on the error.code.
// Plan madde 11's useStepUpAuth() hook will check error.hasCode(
// "operator.mfa_required" | "operator.webauthn_assertion_required") to
// trigger the step-up MFA prompt.

import type {
  Classification,
  Envelope,
  EnvelopeMeta,
  ErrorEntry,
} from "@novemai/error-envelope";

/**
 * Frontend-local synthetic error codes — emitted by `unwrap()` when the
 * backend response either failed before reaching application logic
 * (network/CORS) or violated the envelope contract. These are NOT in
 * @novemai/error-envelope/src/platform-errors.ts because that module's
 * PLATFORM_ERROR_CODES list is backend-emit-only; promoting these into
 * shared exports lands at plan madde 8 follow-up (cross-platform error
 * class promotion).
 *
 * Pinned as a typed const so:
 *   1. the envelope.ts throw branches + the i18n bundle's `errors.code.*`
 *      keys reference the SAME string (typo drift caught at build time);
 *   2. plan madde 8.2's mapErrorToToast can key off them via
 *      `error.hasCode(SYNTHETIC_ERROR_CODES.NETWORK)` instead of literal
 *      matching;
 *   3. the i18n locales test (locales.test.ts) can iterate the values
 *      to assert each synthetic code has a translation in both bundles.
 */
export const SYNTHETIC_ERROR_CODES = {
  /** Wire never reached the server, or response wasn't parseable as an envelope. */
  NETWORK: "platform.network_error",
  /** Server returned 2xx but the body violated the spec 58 envelope shape. */
  INVARIANT: "platform.invariant_violation",
} as const;

export type SyntheticErrorCode =
  (typeof SYNTHETIC_ERROR_CODES)[keyof typeof SYNTHETIC_ERROR_CODES];

/**
 * Thrown by `unwrap()` when the backend returns a failure envelope or a
 * malformed response. Carries the full envelope error array + meta so
 * downstream consumers (toast adapter, step-up auth hook, retry logic)
 * can make decisions on classification + retryability + i18n key.
 */
export class EnvelopeError extends Error {
  readonly errors: readonly ErrorEntry[];
  readonly meta: EnvelopeMeta | undefined;
  /** HTTP status code observed on the wire; 0 when network/CORS fails before status is known. */
  readonly httpStatus: number;

  constructor(input: {
    readonly errors: readonly ErrorEntry[];
    readonly meta?: EnvelopeMeta;
    readonly httpStatus: number;
  }) {
    const primary = input.errors[0];
    // Developer-facing message — combines code + dev message; user-facing
    // localization happens at the toast layer keyed on primary.code.
    const message = primary
      ? `${primary.code}: ${primary.message}`
      : `Envelope error (HTTP ${input.httpStatus}, no errors[] entry)`;
    super(message);
    this.name = "EnvelopeError";
    this.errors = input.errors;
    this.meta = input.meta;
    this.httpStatus = input.httpStatus;
    // Restore the prototype chain — required when transpiling class
    // through tools that target ES5 / older runtimes (Vite targets ES2022
    // so this is defensive but cheap).
    Object.setPrototypeOf(this, EnvelopeError.prototype);
  }

  /** First (most relevant) error entry — most callers need only this for the toast/retry decision. */
  get primary(): ErrorEntry | undefined {
    return this.errors[0];
  }

  /** Quick classification check — `if (err.is("authentication")) signoutRedirect()`. */
  is(classification: Classification): boolean {
    return this.primary?.classification === classification;
  }

  /** Code presence check — `if (err.hasCode("operator.mfa_required")) triggerStepUp()`. */
  hasCode(code: string): boolean {
    return this.errors.some((e) => e.code === code);
  }

  /** True iff the primary error is retryable per spec 58 retryability rules. */
  get retryable(): boolean {
    return this.primary?.retryable === true;
  }

  /** Server-suggested retry delay (ms); set on rate_limited / dependency / unavailable. */
  get retryAfterMs(): number | undefined {
    return this.primary?.retry_after_ms;
  }
}

/**
 * Shape constraint for an openapi-fetch result whose JSON body conforms
 * to the canonical envelope. The payload type `TData` is the value type
 * sitting at body.data on the success branch — i.e., the inner shape
 * the route handler called makeSuccess() with.
 */
export interface EnvelopedFetchResult<TData> {
  readonly data?: Envelope<TData>;
  readonly error?: Envelope<null>;
  readonly response?: Response;
}

/**
 * Unwrap an openapi-fetch result into the success payload, throwing
 * `EnvelopeError` on any failure (envelope or malformed). Generic over
 * `TData` so callers retain the response payload's full type from
 * the codegen pipeline (paths import from src/api/types.gen.ts).
 *
 * Usage:
 *   const data = await unwrap(operatorGraph.GET("/v1/healthz"));
 *   // data: { status: "ok", service: string, version: string }
 *
 * Hook integration (TanStack Query):
 *   useQuery({
 *     queryKey: ["operator", "healthz"],
 *     queryFn: () => unwrap(operatorGraph.GET("/v1/healthz")),
 *   });
 *   // queryFn rejects on EnvelopeError → query.error is the typed throw;
 *   // mapErrorToToast(query.error) renders the user-facing toast.
 */
export async function unwrap<TData>(
  resultPromise: Promise<EnvelopedFetchResult<TData>>,
): Promise<TData> {
  const result = await resultPromise;
  const status = result.response?.status ?? 0;

  // openapi-fetch sets `data` on 2xx and `error` on non-2xx. The body
  // shape is the SAME envelope either way; the split is purely on HTTP
  // status. We pick whichever is present — the envelope-shape contract
  // is what we care about, not which slot openapi-fetch parked it in.
  const body = result.data ?? result.error;

  if (body === undefined) {
    // Network error / CORS reject / non-JSON body / empty 204. There's
    // no envelope to inspect; surface a synthetic dependency error so
    // the toast adapter can still render something coherent.
    throw new EnvelopeError({
      errors: [
        {
          code: SYNTHETIC_ERROR_CODES.NETWORK,
          classification: "dependency",
          message: `HTTP ${status}: empty or non-envelope response body`,
          user_message:
            "We couldn't reach the server. Check your connection and try again.",
          retryable: true,
        },
      ],
      httpStatus: status,
    });
  }

  // Failure branch: errors[] populated.
  const hasErrors = Array.isArray(body.errors) && body.errors.length > 0;
  if (hasErrors) {
    throw new EnvelopeError({
      errors: body.errors as readonly ErrorEntry[],
      ...(body.meta !== undefined ? { meta: body.meta } : {}),
      httpStatus: status,
    });
  }

  // Success branch: data must be non-null/undefined. If it's missing
  // here, the response violated the envelope invariant — rare in
  // practice (the backend's strict-mode hook catches most cases) but
  // defensive in case a response slips through with both data:null
  // AND no errors[].
  if (body.data === null || body.data === undefined) {
    throw new EnvelopeError({
      errors: [
        {
          code: SYNTHETIC_ERROR_CODES.INVARIANT,
          classification: "internal",
          message: `Envelope shape invariant violated: HTTP ${status} with no data and no errors`,
          user_message:
            "Something went wrong. Please try again or contact support.",
          retryable: false,
        },
      ],
      ...(body.meta !== undefined ? { meta: body.meta } : {}),
      httpStatus: status,
    });
  }

  return body.data;
}
